/**
 * @fileoverview Hook for fetching and managing user roles
 * 
 * @description
 * Provides role information for the current authenticated user.
 * Uses react-query for caching and Supabase realtime for live updates.
 * 
 * USAGE:
 * Always use the boolean flags (isModerator, isCurator, isAdmin) instead of
 * checking the role string directly.
 * 
 * @example
 * // ✅ Correct usage for moderator check
 * const { isModerator } = useUserRole();
 * if (!isModerator) return <AccessDenied />;
 * 
 * // ✅ Correct usage for curator features
 * const { isCurator } = useUserRole();
 * {isCurator && <CreateListingButton />}
 * 
 * // ❌ WRONG: Don't check role directly — admin also needs moderator access!
 * const { role } = useUserRole();
 * if (role === 'moderator') {...} // WRONG!
 * 
 * @see src/lib/roles/README.md for full documentation
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/services/logger';
import { fetchUserRoles, getPrimaryRole, type AppRole } from '@/lib/roles';

interface UseUserRoleResult {
  /** Primary/highest role (for display only, use boolean flags for access checks) */
  role: AppRole | null;
  /** All roles the user has */
  roles: AppRole[];
  /** 
   * User has explicit 'curator' role
   * NOTE: Admin/moderator are NOT automatically curators
   */
  isCurator: boolean;
  /** User has 'admin' role */
  isAdmin: boolean;
  /** 
   * User has 'moderator' OR 'admin' role
   * Use this for moderator access checks (admins have moderator access)
   */
  isModerator: boolean;
  /** Roles are being fetched */
  isLoading: boolean;
  /** Error during fetch */
  error: Error | null;
}

export const useUserRole = (): UseUserRoleResult => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  const {
    data: roles = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user_roles', userId],
    enabled: !!userId,
    queryFn: () => fetchUserRoles(userId as string),
    // Roles are security-sensitive; keep it fresh and refetch on focus/reconnect.
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 2,
  });

  // Keep roles in sync if changed in DB (admin actions / role assignment).
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`user-roles:${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_roles', filter: `user_id=eq.${userId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['user_roles', userId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  // Log errors and warn about empty roles
  useEffect(() => {
    if (error) {
      logger.warn('Failed to fetch user roles', { 
        context: { feature: 'auth', function_name: 'useUserRole' }, 
        error 
      });
    }
  }, [error]);

  useEffect(() => {
    if (!isLoading && userId && roles.length === 0) {
      logger.warn('User has no roles assigned', {
        context: { feature: 'auth', function_name: 'useUserRole' },
      });
    }
  }, [isLoading, userId, roles]);

  const role = getPrimaryRole(roles);

  return {
    role,
    roles,
    // CRITICAL: isCurator should ONLY be true if user has explicit 'curator' role
    // Admin/moderator are NOT automatically curators
    isCurator: roles.includes('curator'),
    isAdmin: roles.includes('admin'),
    // Moderator access includes admin
    isModerator: roles.includes('moderator') || roles.includes('admin'),
    isLoading: !!userId && isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
  };
};

export default useUserRole;
